// ============================================================================
// The business name the QM Assistant introduces itself for (D9, G2).
// ----------------------------------------------------------------------------
// assistant-system.md carries two slots, {{BUSINESS_NAME}} and
// {{BUSINESS_DESCRIPTION}}. Before 2026-10-07 the prompt hard-coded "RJ Bird
// Building, an Australian strata-remediation builder operating in Sydney,
// NSW", so every other business's Assistant called itself RJ Bird's on every
// turn, while v6.0.69 had already made every quote print the business's own
// name. RJ Bird keeps that whole sentence; any other business is described
// neutrally.
//
// RJ Bird keeps the name its prompt always used, "RJ Bird Building". Any other
// business gets its trading name (its legal name when it has none). With no
// profile, or a cached profile that belongs to another business, the name is
// NEUTRAL_BUSINESS_NAME, never RJ Bird's.
//
// The same tenant check as business-warranty-note.ts: getInstanceOwner()
// serves its cache for five minutes whoever is signed in, so after a same-tab
// switch the cache can still hold RJ Bird's row.
// ============================================================================

import { getActiveTenantId, getInstanceOwner, getInstanceOwnerCached } from './tenant-resolver';
import { isRjBirdTenantProfile } from './rj-bird-rate-card-sync';

/** What the prompt says when the business is not known. */
export const NEUTRAL_BUSINESS_NAME = 'your business';

/** RJ Bird's name as its Assistant prompt has always written it. */
export const RJ_BIRD_ASSISTANT_BUSINESS_NAME = 'RJ Bird Building';

/** What the prompt says about a business it has no description for. */
export const NEUTRAL_BUSINESS_DESCRIPTION = 'an Australian building business';

/** RJ Bird's description as its Assistant prompt wrote it up to v6.0.69. */
export const RJ_BIRD_ASSISTANT_BUSINESS_DESCRIPTION = 'an Australian strata-remediation builder operating in Sydney, NSW';

function text(value: unknown): string {
    return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
}

/** The name this profile's Assistant introduces itself for. */
export function assistantBusinessNameFor(profile: unknown): string {
    if (!profile || typeof profile !== 'object') return NEUTRAL_BUSINESS_NAME;
    const row = profile as Record<string, unknown>;
    if (!row.tenantId || row.tenantId !== getActiveTenantId()) return NEUTRAL_BUSINESS_NAME;
    if (isRjBirdTenantProfile(row)) return RJ_BIRD_ASSISTANT_BUSINESS_NAME;
    return text(row.tradingName) || text(row.legalName) || NEUTRAL_BUSINESS_NAME;
}

/**
 * How this profile's Assistant describes the business, with the same guards
 * as the name: RJ Bird's own wording for RJ Bird only, the neutral line for
 * every other business, no profile, or a cached profile of another business.
 */
export function assistantBusinessDescriptionFor(profile: unknown): string {
    if (!profile || typeof profile !== 'object') return NEUTRAL_BUSINESS_DESCRIPTION;
    const row = profile as Record<string, unknown>;
    if (!row.tenantId || row.tenantId !== getActiveTenantId()) return NEUTRAL_BUSINESS_DESCRIPTION;
    if (isRjBirdTenantProfile(row)) return RJ_BIRD_ASSISTANT_BUSINESS_DESCRIPTION;
    return NEUTRAL_BUSINESS_DESCRIPTION;
}

/** The name and description from one profile read. Never throws. */
export async function loadAssistantBusinessIdentity(): Promise<{ name: string; description: string }> {
    try {
        const profile = await getInstanceOwner();
        return { name: assistantBusinessNameFor(profile), description: assistantBusinessDescriptionFor(profile) };
    } catch {
        return { name: NEUTRAL_BUSINESS_NAME, description: NEUTRAL_BUSINESS_DESCRIPTION };
    }
}

/**
 * The same answer from the profile already cached, without waiting: for the
 * panel's synchronous legacy prompt builder. The last profile this business
 * read successfully counts; nothing cached yet gives NEUTRAL_BUSINESS_NAME.
 * Never throws.
 */
export function assistantBusinessNameNow(): string {
    try {
        return assistantBusinessNameFor(getInstanceOwnerCached({ orLastGood: true }));
    } catch {
        return NEUTRAL_BUSINESS_NAME;
    }
}
